import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { property } from 'lodash';
import { useImage, useImages } from '@dyl/systems/ImagesManager';
import ObjectPicker from '../ObjectPicker';
import { Box, CircularProgress, FormHelperText, IconButton, Typography } from '@mui/material';
import useStyles from './styles';
import { Edit as EditIcon, Fullscreen as FullscreenIcon, Visibility as ShowIcon, VisibilityOff as HideIcon } from '@mui/icons-material';
import EditOffRoundedIcon from '@mui/icons-material/EditOffRounded';
import cn from 'classnames';
import Lightbox from 'yet-another-react-lightbox';
import useAssetUpload from '@dyl/hooks/useAssetUpload';
import { ImageAssetMimeTypes } from '@dyl/constants/AssetConstants';
import ImageMiniPreview from '../ImageMiniPreview';
import quickDateFormat from '@dyl/utils/quickDateFormat';
import { useSettings } from '@dyl/systems/SettingsManager';

/** @typedef {import('@dyl/systems/ImagesManager').ImageAsset} ImageAsset */

const getId = property('image_id');
const getDisplayName = property('image_id');

/**
 * @param {import('../ObjectPicker').ObjectPickerProps<ImageAsset>} props
 */
export default function ImagePicker({ value, label, helperText, error, isArcPublished, ...other }) {
    const classes = useStyles(other);
    const { images, imagesStatus, refreshImages } = useImages();
    const { src } = useImage(value);

    const { shouldShowImagePreviewsByDefault } = useSettings();

    const [shouldShowListPreviews, setShowListPreviews] = useState(shouldShowImagePreviewsByDefault);
    const [isLightboxOpen, setLightboxOpen] = useState(false);

    const onFullscreenClick = useCallback(event => {
        event.stopPropagation();

        setLightboxOpen(true);
    }, []);

    const baseUpload = useAssetUpload(images);

    const uploadFile = useCallback(file => baseUpload('images', file), [baseUpload]);

    const PreviewUpload = useCallback(function PreviewUpload({ file }) {
        const src = useMemo(() => {
            return file ? URL.createObjectURL(file) : null;
        }, [file]);

        useEffect(() => {
            if (src) {
                return () => URL.revokeObjectURL(src);
            }
        }, [src]);

        return <img src={src} alt={value} className={classes.preview} />;
    }, [classes, value]);

    const columns = useMemo(
        /** @returns {import('@mui/x-data-grid').GridColDef[]} */
        () => [
            { headerName: 'Name', field: 'image_id', flex: 1,
                renderCell({ value }) {
                    return (
                        <>
                            {shouldShowListPreviews && (
                                <ImageMiniPreview imageId={value} style={{ marginRight: 8 }} />
                            )}
                            {value}
                        </>
                    );
                }
            },
            { headerName: 'Last Updated', field: 'updated_at', flex: 1,
                valueFormatter({ value }) {
                    return quickDateFormat(value);
                },
            },
        ], [shouldShowListPreviews]
    );

    const listActions = useMemo(() => [{
        text: shouldShowListPreviews ? 'Hide Previews' : 'Show Previews',
        onClick: () => setShowListPreviews(prev => !prev),
        icon: shouldShowListPreviews ? HideIcon : ShowIcon,
    }], [shouldShowListPreviews]);

    return <>
        <ObjectPicker
            columns={columns}
            data={images}
            value={value}
            isLoading={imagesStatus !== 'complete'}
            getId={getId}
            getDisplayName={getDisplayName}
            searchColumn='image_id'
            refresh={refreshImages}
            allowUpload
            onUploadConfirm={uploadFile}
            uploadAccept={ImageAssetMimeTypes.join()}
            PreviewUploadComponent={PreviewUpload}
            actions={listActions}
            {...other}
        >
            {({ className, ...other }) => (
                <Box className={cn(classes.root, className)}>
                    <Box className={classes.picker}>
                        <img
                            className={classes.image}
                            src={src}
                            alt={value || '(no image selected)'}
                        />

                        {!!label && (
                            <Box className={classes.labelBar}>
                                {label}
                            </Box>
                        )}

                        <Box className={classes.toolbar}>

                            <Box className={classes.titleContainer}>
                                <Typography variant='caption' className={classes.title}>
                                    {value}
                                </Typography>
                            </Box>

                            {(isArcPublished === undefined || isArcPublished) ? (
                                <IconButton color='inherit' {...other} size="large">
                                    <EditIcon fontSize='small' />
                                </IconButton>
                            ) : (
                                <IconButton color='inherit' size="large" disabled sx={{ color: '#fff', '&.Mui-disabled': { color: '#fff', opacity: 1 }}}>
                                    <EditOffRoundedIcon fontSize='small' color='white' />
                                </IconButton>
                            )}

                            <IconButton color='inherit' onClick={onFullscreenClick} size="large" sx={{ color: '#fff', '&.Mui-disabled': { color: '#fff', opacity: 1 }}}>
                                <FullscreenIcon fontSize='small' />
                            </IconButton>
                        </Box>

                        {imagesStatus === 'loading' && (
                            <Box className={classes.loadingWheelContainer}>
                                <CircularProgress color='primary' />
                            </Box>
                        )}
                    </Box>

                    {!!helperText && (
                        <FormHelperText error={error}>
                            <>
                                {helperText}
                            </>
                        </FormHelperText>
                    )}
                </Box>
            )}
        </ObjectPicker>

        {isLightboxOpen && (
            <Lightbox
                open={isLightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={[{ src }]}
            />
        )}

    </>;
}